import React, { FC, useEffect, useState, useRef, useCallback } from "react"
import { useRequest } from "ahooks"
import { getFlooGeo } from "@api/report"
import Env from "@util/env"

/**
 * 显示室内地图
 * @param AMap
 * @param mapIns
 * @param geo
 * example: https://lbs.amap.com/api/javascript-api/example/overlayers/geojson/
 */

type PolygonProps = { visible: boolean; map: any; geo: any }
const Polygon: FC<PolygonProps> = ({ visible, map, geo }) => {
  const ref = useRef<any>()
  const clear = useCallback(() => {
    if (ref.current) {
      ref.current.setMap(null)
      ref.current = null
    }
  }, [ref])

  useEffect(() => {
    if (!visible) {
      clear()
    } else if (map && geo) {
      clear()
      const AMap = window.AMap
      var geojson = new AMap.GeoJSON({
        geoJSON: geo,
        // 还可以自定义getMarker和getPolyline
        getPolygon: function (feature: any, lnglats: any) {
          let prop = feature.properties._parentProperities
          // 计算面积
          // var area = AMap.GeometryUtil.ringArea(lnglats[0])

          return new AMap.Polygon({
            path: lnglats,
            fillOpacity: prop.opacity, // 面积越大透明度越高
            strokeColor: prop.strokeColor || "#12b5ff",
            strokeWeight: prop.strokeWeight,
            fillColor: prop.fillColor || "red",
          })
        },
      })
      geojson.setMap(map)
      ref.current = geojson
    }
    return () => {
      clear()
    }
  }, [map, geo, visible, clear, ref])
  return null
}
/**
 * 显示名称
 * @param AMap
 * @param mapIns
 * @param geo
 * example: https://lbs.amap.com/api/javascript-api/example/marker/labelsmarker-text
 * {"name":"张六庄镇","position":["113.280637","23.125178"],"zooms":[5,13],"zIndex":1,"opacity":1,"text":{"content":"广东","direction":"center","offset":[0,0],"zooms":[3,20],"style":{"fontSize":15,"fontWeight":"normal","fillColor":"#eee","strokeColor":"#c67805","strokeWidth":2}}}
 *
 */
type LabelProps = { visible: boolean; map: any; geo: any }
const Label: FC<LabelProps> = ({ visible, map, geo }) => {
  const ref = useRef<any>()
  const clear = useCallback(() => {
    if (ref.current) {
      ref.current.clear()
      ref.current = null
    }
  }, [ref])
  useEffect(() => {
    if (!visible) {
      clear()
    } else if (map && geo) {
      clear()
      const AMap = window.AMap
      var layer = new AMap.LabelsLayer({
        rejectMapMask: true,
        // 开启标注避让，默认为开启，v1.4.15 新增属性
        collision: true,
        // 开启标注淡入动画，默认为开启，v1.4.15 新增属性
        animation: true,
      })
      map.add(layer)
      geo.features.forEach((feature, i) => {
        let { properties: prop, geometry } = feature
        if (
          !prop.category ||
          ["通道", "非开放区域", "客梯", "货梯", "楼梯"].includes(prop.name)
        ) {
          return
        }

        let conf = {
          name: prop.name,
          position: prop.center,
          zooms: [19, 20],
          // zIndex: 100 + i,
          opacity: 1,
          text: {
            content: prop.name,
            direction: "center",
            offset: [0, 0],
            zooms: [19, 20],
            style: {
              fontSize: 12, // * Env.getScale(),
              fontWeight: "bold",
              fillColor: "#01FEF1",
            },
          },
        }
        var labelsMarker = new AMap.LabelMarker(conf)
        layer.add(labelsMarker)
        ref.current = layer
      })
    }
    return () => {
      clear()
    }
  }, [map, geo, visible, clear, ref])
  return null
}

/**
 * 显示图片
 * @param AMap
 * @param mapIns
 * example: https://lbs.amap.com/api/javascript-api/example/marker/custom-icon/
 * doc: https://lbs.amap.com/api/javascript-api/reference/overlay#PolylineOptions
 */

type IconProps = { visible: boolean; map: any; iconConf: any }
const Icon: FC<IconProps> = ({ visible, map, iconConf }) => {
  const [img, setImg] = useState<string | null>(null)

  const ref = useRef<any>()
  const clear = useCallback(() => {
    if (ref.current) {
      ref.current.forEach((m) => {
        m.setMap(null)
      })
      ref.current = null
    }
  }, [ref])
  useEffect(() => {
    if (!visible) {
      clear()
    } else if (map) {
      clear()
      const AMap = window.AMap
      let size = 100
      var markers = []
      for (let k in iconConf) {
        let c = iconConf[k]
        var m = new AMap.Marker({
          position: new AMap.LngLat(...c.coord),
          icon: new AMap.Icon({
            size: new AMap.Size(size, size),
            image: c.icon_c,
            imageSize: new AMap.Size(size, size),
            // imageOffset: new AMap.Pixel(-100, -100),
          }),
          offset: new AMap.Pixel(-size / 2, -size / 2),
          clickable: true,
          // draggable: true,
          zIndex: 1000,
        })
        // AMap.event.addListener(m, "click", show)
        m.on("click", (e) => {
          console.log("c", c.icon)
          setImg(c.icon)
        })
        // m.emit("click", { target: m })
        var polyline = new AMap.Polyline({
          path: [new AMap.LngLat(...c.coord), new AMap.LngLat(...c.from)],
          strokeStyle: "dashed",
          strokeColor: "#BAE3FF",
        })
        markers.push(m, polyline)
      }

      map.add(markers, {
        zIndex: 1000,
      })
      ref.current = markers
    }
    return () => {
      clear()
    }
  }, [map, visible, clear, ref])

  if (!img) {
    return null
  }
  return (
    <div
      className="mask-fixed"
      onClick={() => {
        setImg(null)
      }}
    >
      <img src={img} alt="img" />
    </div>
  )
}

/**
 * LayerIndoor
 * @param param0
 */
const LayerIndoor: FC<any> = ({ map, visible }) => {
  const { data } = useRequest(getFlooGeo, {})

  if (!data) {
    return null
  }

  return (
    <div>
      <Polygon visible={visible} map={map} geo={data.geo} />
      <Label visible={visible} map={map} geo={data.geo} />
      <Icon visible={visible} map={map} iconConf={data.confImgLabel} />
    </div>
  )
}

export default LayerIndoor
